import React, { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import moment from "moment"
import { URL } from "../../../store/actions/depannageActions"
import { handleErrors } from "../../../store/actions/apiHelpers"
import { COLORS } from "../../../utilities/Constants"
import ChoixBulles from "../../basics/input/ChoixBulles"

// GE-75 (Jordan 19.10) : calculateur COMPLET du tarif privé à la création,
// écran « Calcul du tarif » de la maquette client (docs/depannage-prive/
// Tarifs dépannage privés_V_Def.html) à nos couleurs : les 4
// réponses (GE-66) + France, km en charge, temps suppl., grutage ; la nuit,
// relivraison le lendemain (Oui par défaut) et son dépôt. Allégé le 20.10
// (Jordan) : sans les titres d'étapes ni le cadre de la maquette.
// Les DEUX prix complets (sur place / transporté, ou devis à part) et la
// phrase « À annoncer au client » viennent du SERVEUR (api/prive/apercu,
// moteur svp/prive_tarifs) — plus de calcul de grille ici (fin du « dès X »
// de C3 du 15.10). Jour / Nuit PRÉ-REMPLI (GE-66 A1), toujours modifiable.
export const QUESTIONS_PRIVE = [
	{
		cle: "prive_lieu",
		label: "Lieu",
		choix: [
			["ROU", "Route"],
			["SOU", "Sous-sol"],
			["AUT", "Autoroute"],
		],
	},
	{
		cle: "prive_situation",
		label: "Situation",
		choix: [
			["PAN", "Panne"],
			["ACC", "Accident"],
		],
	},
	{
		cle: "prive_moment",
		label: "Moment",
		choix: [
			["JOUR", "Jour"],
			["NUIT", "Nuit"],
		],
	},
	{
		cle: "prive_poids",
		label: "Poids",
		choix: [
			["M2T", "Moins de 2 t"],
			["P2T", "Plus de 2 t"],
		],
	},
]

export const reponsesPriveCompletes = (dm) =>
	QUESTIONS_PRIVE.every((q) => !!dm[q.cle])

// 4 réponses requises dans le FORMULAIRE pour une NOUVELLE mission privée :
// intervention, et transport depuis Q4 (Jordan 15.10 : toujours le tarif
// « transporté ») ; jamais une livraison (prix du bureau) ni une mission
// existante (le serveur reste tolérant).
export const reponsesPriveRequises = (dm) => dm.id === null && !dm.est_livraison

// Même règle que police_tarifs.est_nuit (18:30 – 07:00, samedi et dimanche
// entiers, fériés de la grille) — pour un RDV d'agenda (revue A1, 🟠13).
export const estNuit = (m, feries = []) => {
	if (m.day() === 0 || m.day() === 6) return true
	if (feries.includes(m.format("YYYY-MM-DD"))) return true
	const minutes = m.hours() * 60 + m.minutes()
	return minutes >= 18 * 60 + 30 || minutes < 7 * 60
}

const rendezVous = (dm) => {
	if (!dm.date_rendez_vous) return null
	const m = moment(dm.date_rendez_vous, "DD-MM-YYYY HH:mm", true)
	return m.isValid() ? m : null
}

// Moment pré-rempli : celui du rendez-vous s'il est posé, sinon maintenant.
// `sansRdv` : mission dispatchée tout de suite (« Créer et dispatcher » retire
// le RDV, revue passe 3 🟡 F) → maintenant.
export const momentPrevu = (dm, grille, sansRdv = false) => {
	const rdv = sansRdv ? null : rendezVous(dm)
	if (rdv) return estNuit(rdv, grille.feries || []) ? "NUIT" : "JOUR"
	return grille.moment_actuel
}

// Options de la maquette (codes de svp/prive_tarifs.py). « ≤ 20 » = aucun
// supplément km. France : + « 100 km et plus » (devis à part), comme la
// tablette (décision Jordan 19.10 ; la maquette ne l'a qu'en aide).
export const FRANCE_PRIVE = [
	["", "Non"],
	["Z1", "Zone 1"],
	["Z2", "Zone 2"],
	["DEVIS", "100 km et plus"],
]
// Livraison en France (étape 2) : les trois choix de la maquette.
export const FRANCE_LIVRAISON = FRANCE_PRIVE.slice(0, 3)
export const KM_PRIVE = [
	["", "≤ 20"],
	["T1", "21–30"],
	["T2", "31–40"],
	["T3", "41–50"],
	["DEVIS", "> 50"],
]
export const TEMPS_MAX = 16

// « 0 min », « 30 min », « 1 h », « 1 h 30 »…
export const texteDemiHeures = (n) => {
	if (!n) return "0 min"
	const h = Math.floor(n / 2)
	if (!h) return "30 min"
	return n % 2 ? `${h} h 30` : `${h} h`
}

// Grutage : « Non », puis 1 h d'office (2 demi-heures), puis par ½ h.
export const grutageSuivant = (n, pas) => {
	const v = n || 0
	if (pas > 0) return v === 0 ? 2 : Math.min(TEMPS_MAX, v + 1)
	return v <= 2 ? 0 : v - 1
}

// Corps de api/prive/apercu (mêmes clés que la mission).
export const corpsApercu = (dm) => ({
	prive_lieu: dm.prive_lieu || "",
	prive_situation: dm.prive_situation || "",
	prive_moment: dm.prive_moment || "",
	prive_poids: dm.prive_poids || "",
	prive_km: dm.prive_france ? "" : dm.prive_km || "",
	prive_france: dm.prive_france || "",
	prive_temps: dm.prive_temps || 0,
	prive_grutage: dm.prive_grutage || 0,
	prive_sous_contrat: !!dm.prive_sous_contrat,
	prive_relivraison_prevue:
		dm.prive_moment === "NUIT" && !!dm.prive_relivraison_prevue,
	prive_france_livraison: dm.prive_relivraison_prevue
		? dm.prive_france_livraison || ""
		: "",
	type: dm.type || 0,
})

const idDe = (poi) => (poi && typeof poi === "object" ? poi.id : poi) || null

// Montants comme la maquette : « 150 », « 152.50 ».
export const chf = (n) =>
	Number.isInteger(Number(n)) ? `${Number(n)}` : Number(n).toFixed(2)

const ZONE = (code) => (code.endsWith("Z2") ? "2" : "1")

// Détail d'un prix sous la tuile, formule de la maquette : suppléments des
// deux prix (« Base + France zone 1 +100, 2 × 30 min de jour +180, grutage
// 1 h +360 »), puis au transporté « + km en charge +40 » et « + livraison
// +150 + France zone 1 +100 » ; « Sans supplément » sinon.
export const detailPrix = (prix) => {
	if (!prix) return ""
	if (prix.devis) return "Devis à part"
	const lignes = (prix.lignes || []).slice(1)
	const somme = (filtre) =>
		lignes.filter(filtre).reduce((s, l) => s + Number(l.montant || 0), 0)
	const parts = []
	lignes
		.filter((l) => /^FR_Z/.test(l.code))
		.forEach((l) =>
			parts.push(`France zone ${ZONE(l.code)} +${chf(l.montant)}`),
		)
	lignes
		.filter((l) => /^TEMPS_/.test(l.code))
		.forEach((l) =>
			parts.push(
				`${l.quantite} × 30 min de ${l.code.endsWith("NUIT") ? "nuit" : "jour"} +${chf(l.montant)}`,
			),
		)
	const grue = lignes.filter((l) => /^GRUTAGE_/.test(l.code))
	if (grue.length) {
		const demi =
			2 +
			grue
				.filter((l) => /TRANCHE/.test(l.code))
				.reduce((s, l) => s + Number(l.quantite || 0), 0)
		parts.push(
			`grutage ${texteDemiHeures(demi)} +${chf(somme((l) => /^GRUTAGE_/.test(l.code)))}`,
		)
	}
	// Revue 20.10 (R10) : toute autre ligne garde son libellé et son montant
	// (jamais avalée dans « Sans supplément »).
	lignes
		.filter(
			(l) => !/^(FR_Z|TEMPS_|GRUTAGE_|KM_CH_|LIV_)/.test(l.code || ""),
		)
		.forEach((l) => parts.push(`${l.libelle || l.code} +${chf(l.montant)}`))
	let fin = ""
	const km = somme((l) => /^KM_CH_/.test(l.code))
	if (km) fin += ` + km en charge +${chf(km)}`
	const liv = lignes.find((l) => l.code === "LIV_FORFAIT")
	if (liv) {
		fin += ` + livraison +${chf(liv.montant)}`
		lignes
			.filter((l) => /^LIV_FR_Z/.test(l.code))
			.forEach(
				(l) =>
					(fin += ` + France zone ${ZONE(l.code)} +${chf(l.montant)}`),
			)
	}
	const supp = parts.join(", ")
	if (!supp && !fin) return "Sans supplément"
	return `${supp ? `Base + ${supp}` : "Base"}${fin}`
}

// Revue 20.10 (R6) : cause du devis au transporté, selon le serveur.
export const detailDevisTransporte = (tr) =>
	tr.cause_devis === "france"
		? "France 100 km et plus"
		: "Plus de 50 km en charge"

// Cause du transport d'office en clair (« autoroute », « accident ») ; vide
// pour un transport demandé (la tuile le dit déjà, rework Jordan 20.10).
export const causeTransport = (dm) =>
	[
		dm.prive_lieu === "AUT" && "autoroute",
		dm.prive_situation === "ACC" && "accident",
	]
		.filter(Boolean)
		.join(" et ")

const Aide = ({ texte }) => {
	const [ouvert, setOuvert] = useState(false)
	return (
		<>
			<button
				type="button"
				style={st.info}
				aria-expanded={ouvert}
				title="Aide"
				onClick={() => setOuvert((o) => !o)}
			>
				i
			</button>
			{ouvert && <div style={st.why}>{texte}</div>}
		</>
	)
}

const Question = ({ label, aide, children }) => (
	<div style={st.question}>
		<div style={st.qTete}>
			<span style={st.qLabel}>{label}</span>
			{aide && <Aide texte={aide} />}
		</div>
		{children}
	</div>
)

const Stepper = ({ label, valeur, texte, onMoins, onPlus, aide }) => (
	<Question label={label} aide={aide}>
		<div style={st.stepper}>
			<button
				type="button"
				style={st.pas}
				onClick={onMoins}
				disabled={!valeur}
			>
				−
			</button>
			<span style={st.pasValeur}>{texte}</span>
			<button
				type="button"
				style={st.pas}
				onClick={onPlus}
				disabled={valeur >= TEMPS_MAX}
			>
				+
			</button>
		</div>
	</Question>
)

const Resultat = ({ apercu, requis, dm }) => {
	if (!apercu) return <div style={st.attente}>Calcul du tarif…</div>
	if (apercu.incomplet)
		return (
			<div
				style={{
					...st.attente,
					color: requis ? COLORS.danger : COLORS.textMuted,
				}}
			>
				Les 4 réponses (lieu, situation, moment, poids) donnent le
				tarif.
			</div>
		)
	if (apercu.sous_contrat)
		return (
			<>
				<div style={{ ...st.tuile, ...st.tuileContrat }}>
					<span style={st.tuileTitre}>Client sous contrat</span>
					<span style={st.tuilePrix}>Pas de tarif</span>
				</div>
				<div style={st.annonce}>
					<span style={st.annonceTitre}>À dire au client</span>
					{apercu.a_annoncer}
				</div>
				{/* Consigne du dépanneur : pas à la création (bureau). */}
				{dm.id !== null && (
					<div style={st.explication}>
						Remplir la fiche normalement (prestations, km, temps)
						pour la facturation.
					</div>
				)}
			</>
		)
	const { sp, tr } = apercu
	const prix = (p) => (p.devis ? "Devis à part" : `${chf(p.total)} CHF`)
	return (
		<>
			<div style={st.tuiles}>
				{sp ? (
					<>
						<div style={st.tuile}>
							<span style={st.tuileTitre}>Réparé sur place</span>
							<span style={st.tuilePrix}>{prix(sp)}</span>
							<span style={st.tuileDetail}>{detailPrix(sp)}</span>
						</div>
						<div style={st.tuile}>
							<span style={st.tuileTitre}>Transporté</span>
							<span style={st.tuilePrix}>{prix(tr)}</span>
							<span style={st.tuileDetail}>
								{tr.devis
									? detailDevisTransporte(tr)
									: detailPrix(tr)}
							</span>
						</div>
					</>
				) : (
					<div style={{ ...st.tuile, ...st.tuileOffice }}>
						<span style={st.tuileTitre}>Transport d'office</span>
						<span style={st.tuilePrix}>{prix(tr)}</span>
						<span style={st.tuileDetail}>
							{tr.devis
								? detailDevisTransporte(tr)
								: detailPrix(tr)}
						</span>
					</div>
				)}
			</div>
			<div style={st.annonce}>
				<span style={st.annonceTitre}>À annoncer au client</span>
				{apercu.a_annoncer}
			</div>
			{!sp && causeTransport(dm) && (
				<div style={st.explication}>
					Pas de prix sur place : {causeTransport(dm)}.
				</div>
			)}
		</>
	)
}

const ReponsesPrive = ({
	dm,
	update,
	requis,
	sansRdv = false,
	onDepotDestination = null,
	onRendreDestination = null,
}) => {
	const { token } = useSelector((s) => s.navInfo.login)
	const [grille, setGrille] = useState(null)
	const [parcs, setParcs] = useState([])
	const [apercu, setApercu] = useState(null)
	const creation = dm.id === null
	// Revue 20.10 (R4) : mission partie (≥ 30) → relivraison en lecture
	// seule (la dépose est au terrain) ; en édition, rien n'est posé sans
	// geste de l'utilisateur.
	const figee = !creation && (dm.etat || 0) >= 30
	// Pré-remplissage : à l'arrivée de la grille, puis à chaque changement
	// de date de RDV (posée, modifiée ou retirée, ou écartée par un dispatch
	// immédiat) TANT QUE la réponse est celle pré-remplie ; une réponse
	// choisie ou effacée à la main reste.
	const prerempli = useRef(null)
	useEffect(() => {
		if (!grille || !grille.moment_actuel || !creation) return
		const intact =
			prerempli.current === null
				? !dm.prive_moment
				: dm.prive_moment === prerempli.current
		if (!intact) return
		const prevu = momentPrevu(dm, grille, sansRdv)
		prerempli.current = prevu
		if (prevu !== dm.prive_moment) update("prive_moment", prevu)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [grille, dm.date_rendez_vous, sansRdv])
	useEffect(() => {
		let vivant = true
		const entetes = { headers: { Authorization: `Bearer ${token}` } }
		fetch(`${URL}api/prive/grille`, entetes)
			.then(handleErrors)
			.then((r) => r.json())
			.then((g) => vivant && setGrille(g))
			.catch(() => {})
		fetch(`${URL}api/points_interets`, entetes)
			.then(handleErrors)
			.then((r) => r.json())
			.then((p) => vivant && setParcs((p && p.parcs) || []))
			.catch(() => {})
		return () => {
			vivant = false
		}
	}, [token])
	// À la création : la nuit, « Relivrer le lendemain ? » revient à Oui à
	// chaque changement de situation ou de moment ; de jour, remis à zéro.
	const depot = idDe(dm.destination_intermediaire)
	const sansRelivraison = () => {
		update("prive_relivraison_prevue", false)
		update("prive_france_livraison", "")
		update("destination_intermediaire", null)
	}
	useEffect(() => {
		if (!creation) return
		if (dm.prive_moment === "NUIT") {
			update("prive_relivraison_prevue", true)
			if (onRendreDestination) onRendreDestination()
		} else if (dm.prive_relivraison_prevue) {
			sansRelivraison()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dm.prive_moment, dm.prive_situation])
	// Dépôt de la relivraison : le premier (ou le seul) parc par défaut —
	// création de nuit seulement (R4).
	useEffect(() => {
		if (
			creation &&
			dm.prive_moment === "NUIT" &&
			dm.prive_relivraison_prevue &&
			!depot &&
			parcs.length
		)
			update("destination_intermediaire", parcs[0].id)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dm.prive_relivraison_prevue, dm.prive_moment, parcs.length])
	// Aperçu SERVEUR des deux prix (debounce).
	const corps = JSON.stringify(corpsApercu(dm))
	useEffect(() => {
		let vivant = true
		const t = setTimeout(() => {
			fetch(`${URL}api/prive/apercu`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: corps,
			})
				.then(handleErrors)
				.then((r) => r.json())
				.then((a) => vivant && setApercu(a))
				.catch(() => {})
		}, 250)
		return () => {
			vivant = false
			clearTimeout(t)
		}
	}, [corps, token])

	const nuit = dm.prive_moment === "NUIT"
	const forfait =
		grille && grille.relivraison && grille.relivraison.forfait
			? grille.relivraison.forfait
			: 150
	const relivrer = (oui) => {
		update("prive_relivraison_prevue", oui)
		if (oui) {
			if (!depot && parcs.length)
				update("destination_intermediaire", parcs[0].id)
			if (onRendreDestination) onRendreDestination()
			return
		}
		// Non : le client récupère au dépôt — le dépôt devient la
		// destination finale, pas de dépose intermédiaire ni de suite.
		update("prive_france_livraison", "")
		const choisi = parcs.find((p) => p.id === depot) || parcs[0]
		update("destination_intermediaire", null)
		if (choisi && onDepotDestination) onDepotDestination(choisi)
	}
	const bulles = (cle, options, extra = {}) => (
		<ChoixBulles
			label={cle}
			niveau={2}
			options={options}
			valeur={dm[cle] || ""}
			onChange={(code) => update(cle, code || "")}
			{...extra}
		/>
	)

	return (
		<div style={st.bloc}>
			{QUESTIONS_PRIVE.map((q) => (
				<Question
					key={q.cle}
					label={
						q.cle === "prive_poids" ? "Poids du véhicule" : q.label
					}
					aide={AIDES[q.cle]}
				>
					<ChoixBulles
						label={q.label}
						niveau={2}
						deselectionnable
						options={q.choix}
						valeur={dm[q.cle] || null}
						onChange={(code) => {
							update(q.cle, code || "")
							// R4 : en édition, passer en Jour (geste) retire
							// la relivraison, sauf mission partie (terrain).
							if (
								q.cle === "prive_moment" &&
								!creation &&
								!figee &&
								code !== "NUIT" &&
								dm.prive_relivraison_prevue
							)
								sansRelivraison()
						}}
					/>
				</Question>
			))}
			<div style={st.separateur} />
			<Question label="Supplément France" aide={AIDES.france}>
				{bulles("prive_france", FRANCE_PRIVE)}
			</Question>
			<Question
				label="Km en charge (Suisse)"
				aide={
					dm.prive_france
						? "Trajet en France : les zones France s'appliquent à la place des km suisses (pas de cumul)."
						: nuit && dm.prive_relivraison_prevue
							? `Du lieu de la panne à notre dépôt. ${AIDES.km}`
							: `Du lieu de la panne à la destination. ${AIDES.km}`
				}
			>
				{bulles("prive_km", KM_PRIVE, { disabled: !!dm.prive_france })}
			</Question>
			<div style={st.deuxColonnes}>
				<Stepper
					label="Temps suppl."
					valeur={dm.prive_temps || 0}
					texte={texteDemiHeures(dm.prive_temps || 0)}
					onMoins={() =>
						update(
							"prive_temps",
							Math.max(0, (dm.prive_temps || 0) - 1),
						)
					}
					onPlus={() =>
						update(
							"prive_temps",
							Math.min(TEMPS_MAX, (dm.prive_temps || 0) + 1),
						)
					}
					aide={AIDES.temps}
				/>
				<Stepper
					label="Grutage"
					valeur={dm.prive_grutage || 0}
					texte={
						dm.prive_grutage
							? texteDemiHeures(dm.prive_grutage)
							: "Non"
					}
					onMoins={() =>
						update(
							"prive_grutage",
							grutageSuivant(dm.prive_grutage, -1),
						)
					}
					onPlus={() =>
						update(
							"prive_grutage",
							grutageSuivant(dm.prive_grutage, 1),
						)
					}
					aide={AIDES.grutage}
				/>
			</div>
			{nuit && (
				<>
					<div style={st.separateur} />
					<Question label="Relivrer le lendemain ?">
						<ChoixBulles
							label="Relivrer le lendemain ?"
							niveau={2}
							options={[
								["1", `Oui · forfait ${forfait}`],
								["0", "Non"],
							]}
							valeur={dm.prive_relivraison_prevue ? "1" : "0"}
							onChange={(v) => relivrer(v === "1")}
							disabled={figee}
						/>
					</Question>
					{figee && (
						<div style={st.aide}>
							Mission partie : la relivraison et le dépôt se
							modifient sur la tablette.
						</div>
					)}
					{dm.prive_relivraison_prevue && (
						<>
							<Question
								label="Livraison en France"
								aide={`Le véhicule passe la nuit à notre dépôt. Livraison de jour au forfait de ${forfait} CHF, sans calcul de km. Si la livraison a lieu en France, le supplément France s'ajoute. Accident de nuit : en principe pas de livraison directe au garage, le véhicule reste au dépôt pour livraison le lendemain.`}
							>
								{bulles(
									"prive_france_livraison",
									FRANCE_LIVRAISON,
									{ disabled: figee },
								)}
							</Question>
							<Question label="Dépôt de nuit">
								<select
									style={st.select}
									value={depot || ""}
									disabled={figee}
									onChange={(e) =>
										update(
											"destination_intermediaire",
											parseInt(e.target.value) || null,
										)
									}
								>
									{(!parcs.length || !depot) && (
										<option value="">
											{parcs.length
												? "Pas encore choisi"
												: "Aucun dépôt"}
										</option>
									)}
									{parcs.map((p) => (
										<option key={p.id} value={p.id}>
											{p.label}
										</option>
									))}
								</select>
							</Question>
							<div style={st.aide}>
								La destination saisie sera celle de la
								relivraison.
							</div>
						</>
					)}
				</>
			)}
			<div style={st.separateur} />
			<label style={st.contrat}>
				<input
					type="checkbox"
					style={st.case}
					checked={!!dm.prive_sous_contrat}
					onChange={() =>
						update("prive_sous_contrat", !dm.prive_sous_contrat)
					}
				/>
				<span style={st.qLabel}>Client sous contrat</span>
				<span style={st.contratSous}>
					Rutsch, Schulthess, IVS Size, DHL
				</span>
			</label>
			{dm.prive_relivraison ? (
				// Passe 5 (🟡5) : une relivraison se facture au forfait, pas
				// sur la grille Sur place / Transporté.
				<div style={st.attente}>Forfait relivraison : {forfait}.–</div>
			) : (
				<Resultat apercu={apercu} requis={requis} dm={dm} />
			)}
		</div>
	)
}

const AIDES = {
	prive_lieu:
		"Sous-sol : prix de la route + une demi-heure supplémentaire (90 CHF de jour, 120 CHF la nuit), sur place comme transporté.",
	prive_moment:
		"Nuit : 18h30 – 7h00, tout le week-end (vendredi 18h30 – lundi 7h00) et les jours fériés. Choisi automatiquement selon l'heure actuelle ; pour un jour férié mobile (Pâques, Ascension, Pentecôte, Jeûne genevois), sélectionner Nuit à la main.",
	france: "Dès qu'une partie du trajet a lieu en France. On compte les km de la boucle complète, de la base à la base. Zone 1 : jusqu'à 50 km. Zone 2 : de 51 à 99 km. À partir de 100 km : devis à part.",
	km: "20 km compris, puis +40 CHF par tranche de 10 km, jour comme nuit. Au-delà de 50 km : devis à part.",
	temps: "Le forfait comprend 30 min sur place (chargement complet). Chaque tranche complète de 30 min au-delà, ou d'attente (douane, dédouanement), est facturée : 20 min de plus = rien, 40 min de plus = une tranche.",
	grutage:
		"Grutage à 2 personnes, première heure facturée d'office. De jour : 360 CHF (2 × 180 CHF/h), puis 180 CHF par tranche de 30 min. De nuit : 480 CHF (2 × 240 CHF/h), puis 240 CHF par tranche de 30 min.",
}

const st = {
	bloc: {
		display: "flex",
		flexDirection: "column",
		gap: 12,
		marginBottom: 18,
	},
	question: { display: "flex", flexDirection: "column", gap: 6 },
	qTete: { display: "flex", alignItems: "center", gap: 6 },
	qLabel: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: COLORS.textMuted,
	},
	info: {
		width: 18,
		height: 18,
		borderRadius: 9,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		fontSize: 11,
		fontWeight: 700,
		lineHeight: "16px",
		padding: 0,
		cursor: "pointer",
	},
	why: {
		fontSize: 12,
		color: COLORS.textMuted,
		backgroundColor: COLORS.bg,
		borderRadius: 8,
		padding: "8px 10px",
	},
	separateur: { height: 1, backgroundColor: COLORS.border },
	deuxColonnes: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: 12,
	},
	stepper: { display: "flex", alignItems: "center", gap: 10 },
	pas: {
		width: 30,
		height: 30,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		fontSize: 16,
		fontWeight: 700,
		color: COLORS.text,
		cursor: "pointer",
	},
	pasValeur: {
		minWidth: 64,
		textAlign: "center",
		fontSize: 14,
		fontWeight: 700,
		color: COLORS.text,
	},
	select: {
		height: 32,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		padding: "0 10px",
		fontSize: 13,
		maxWidth: 320,
	},
	aide: { fontSize: 12, color: COLORS.textMuted },
	contrat: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		cursor: "pointer",
	},
	case: { width: 18, height: 18, margin: 0, cursor: "pointer" },
	contratSous: { fontSize: 12.5, color: COLORS.textLight },
	tuiles: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
	tuile: {
		display: "flex",
		flexDirection: "column",
		gap: 4,
		padding: "12px 14px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
	},
	tuileOffice: { gridColumn: "1 / -1" },
	tuileContrat: {
		borderColor: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
	tuileTitre: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: COLORS.textMuted,
	},
	tuilePrix: { fontSize: 22, fontWeight: 700, color: COLORS.text },
	tuileDetail: { fontSize: 12, color: COLORS.textMuted },
	annonce: {
		display: "flex",
		flexDirection: "column",
		gap: 4,
		padding: "10px 14px",
		borderRadius: 10,
		border: `1px solid ${COLORS.warning}`,
		backgroundColor: COLORS.warningLight,
		fontSize: 13.5,
		color: COLORS.text,
	},
	annonceTitre: {
		fontSize: 11,
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: COLORS.warning,
	},
	explication: { fontSize: 12, color: COLORS.textMuted },
	attente: { fontSize: 13, color: COLORS.textMuted },
}

export default ReponsesPrive
